/**
 * What each guided tour says, and what it points at.
 *
 * Anchors are `data-tour="…"` attributes on the real controls rather than CSS
 * class selectors: a class is a styling detail that gets renamed, while a
 * data-tour attribute exists only for this and shows up in a grep when someone
 * moves the button.
 *
 * A step whose anchor is absent is SKIPPED, not shown against the void. That
 * matters because half these controls are conditional — the Signal pill only
 * mounts once its status arrives, Strategies only when the runner is enabled,
 * and the trade button only for users who may trade.
 */
import {
  TOUR_MAIN,
  TOUR_SIGNAL_ORDERS,
  TOUR_STRATEGIES,
  type TourId,
} from "@/state/slices/tourSlice";

/** Where the bubble sits relative to its anchor. Flipped automatically when it
 *  would leave the viewport, so this is a preference rather than a promise. */
export type Placement = "top" | "bottom" | "left" | "right";

export interface TourStep {
  /** Stable id — used as the React key and in the aria description. */
  id: string;
  /** `data-tour` value of the element to spotlight. */
  anchor: string;
  title: string;
  body: string;
  placement: Placement;
}

export const TOURS: Record<TourId, { steps: TourStep[] }> = {
  [TOUR_MAIN]: {
    steps: [
      {
        id: "add-tab",
        anchor: "add-tab",
        title: "Open a chart",
        body: "Every chart lives in its own tab. Add as many as you like — each one keeps its own symbol, timeframe and drawings.",
        placement: "bottom",
      },
      {
        id: "signal-tab",
        anchor: "signal-tab",
        title: "Signal",
        body: "A read-only view of the reference bot's live chart, with the orders it is running right now. Nothing here touches your own account.",
        placement: "bottom",
      },
      {
        id: "strategies-tab",
        anchor: "strategies-tab",
        title: "Strategies",
        body: "Dozens of trading strategies running live on a demo account. Pick the ones you want to follow and watch their orders on the chart.",
        placement: "bottom",
      },
      {
        id: "workspace",
        anchor: "workspace",
        title: "Workspaces",
        body: "Your whole layout — tabs, symbols, drawings — saved under a name. Switch between setups for different sessions or instruments.",
        placement: "bottom",
      },
      {
        id: "connect-mt5",
        anchor: "connect-mt5",
        title: "Trade your own account",
        body: "Connect your MT5 account to trade from the chart. Until you do, everything you see is read-only.",
        placement: "bottom",
      },
      {
        id: "notes",
        anchor: "notes",
        title: "Notes",
        body: "Jot down what you are seeing. Each note remembers the symbol and timeframe you wrote it on, and can hold screenshots.",
        placement: "left",
      },
    ],
  },

  // Plays the first time the Strategies view is opened — the controls it points
  // at do not exist until then.
  [TOUR_STRATEGIES]: {
    steps: [
      {
        id: "strategy-picker",
        anchor: "strategy-picker",
        title: "Choose your strategies",
        body: "Browse every strategy with its live status and 30-day record, then pick the ones to show. Select as many as you like.",
        placement: "bottom",
      },
      {
        id: "strategy-selection",
        anchor: "strategy-selection",
        title: "Your selection",
        body: "The ones you picked, grouped here. Click any single strategy to show it on its own; click it again to bring the rest back.",
        placement: "bottom",
      },
      {
        id: "strategy-symbol",
        anchor: "strategy-symbol",
        title: "Switch instrument",
        body: "Strategies trade gold and Bitcoin. The chart shows one at a time — switch here, and the list follows.",
        placement: "bottom",
      },
      {
        id: "strategy-orders",
        anchor: "strategy-orders",
        title: "Their open orders",
        body: "Opens a panel listing every running position and pending order of the strategies you selected, grouped by strategy.",
        placement: "left",
      },
    ],
  },

  // Plays the first time the Signal orders panel is opened.
  [TOUR_SIGNAL_ORDERS]: {
    steps: [
      {
        id: "signal-orders-panel",
        anchor: "signal-orders",
        title: "Signal orders",
        body: "The reference bot's live positions and pending orders. It closes when you leave the Signal chart, and reopens from this button.",
        placement: "left",
      },
    ],
  },
};

export function stepsFor(tour: TourId): TourStep[] {
  return TOURS[tour]?.steps ?? [];
}

/** The anchor element for a step, or null when that control is not on screen.
 *
 *  "On screen" means laid out with a real size, not merely present in the DOM: a
 *  hidden or zero-sized control would otherwise get a degenerate spotlight and a
 *  bubble pointing at the top-left corner. */
export function anchorEl(step: TourStep): HTMLElement | null {
  const el = document.querySelector<HTMLElement>(`[data-tour="${step.anchor}"]`);
  if (!el || el.offsetParent === null) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? el : null;
}
